import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutGrid, LayoutList } from 'lucide-react';
import { BACKEND_URL } from '../../config';
import './MyReports.css'; // .drag-scroll-row (scrollbar hiding for DragScrollRow)

// Shared vocabulary for the Reports page and the Interactive Dashboard (kanban).
// Both surfaces show the SAME data under the same rules — the dashboard is only a
// different arrangement of it — so the tab keys, endpoints, transition rules and
// search behaviour all live here rather than being written twice and drifting.

// Tab keys. 'active' is the Forwarded tab: the key predates the rename and is
// what already lives in ?tab= URLs and sessionStorage keys, so it stays.
export type ReportTab = 'received' | 'progress' | 'active' | 'closed' | 'sent';

export const AUTHORITY_TABS: ReportTab[] = [
	'received',
	'progress',
	'closed',
	'active',
];

export const TAB_LABELS: Record<ReportTab, string> = {
	received: 'Received',
	progress: 'In Progress',
	active: 'Forwarded',
	closed: 'Closed',
	sent: 'Sent',
};

export type TabCounts = {
	received: number;
	inProgress: number;
	forwarded: number;
	closed: number;
	sent: number;
};

export const EMPTY_COUNTS: TabCounts = {
	received: 0,
	inProgress: 0,
	forwarded: 0,
	closed: 0,
	sent: 0,
};

export const countForTab = (counts: TabCounts, tab: ReportTab): number => {
	switch (tab) {
		case 'received':
			return counts.received;
		case 'progress':
			return counts.inProgress;
		case 'active':
			return counts.forwarded;
		case 'closed':
			return counts.closed;
		case 'sent':
		default:
			return counts.sent;
	}
};

/**
 * Endpoint for one page of a tab, with the active search term folded in.
 *
 * Search is server-side for the same reason pagination is: filtering the pages
 * already in memory would silently hide every match that hasn't been scrolled to
 * yet, and the tab counts (which the server computes over the whole set) would
 * disagree with the list.
 */
export const buildReportsUrl = (
	tab: ReportTab,
	targetPage: number,
	pageSize: number,
	search: string
): string => {
	const params = new URLSearchParams();
	params.set('limit', String(pageSize));
	params.set('offset', String((targetPage - 1) * pageSize));
	if (search.trim()) params.set('search', search.trim());

	switch (tab) {
		case 'received':
			params.set('isClosed', 'false');
			params.set('inProgress', 'false');
			return `${BACKEND_URL}/api/getReportsForAuthority?${params}`;
		case 'progress':
			params.set('isClosed', 'false');
			params.set('inProgress', 'true');
			return `${BACKEND_URL}/api/getReportsForAuthority?${params}`;
		case 'active':
			return `${BACKEND_URL}/api/getActiveReportsForAuthority?${params}`;
		case 'closed':
			return `${BACKEND_URL}/api/getClosedReportsForAuthority?${params}`;
		case 'sent':
		default:
			return `${BACKEND_URL}/api/myReports?${params}`;
	}
};

export const fetchTabCounts = async (search: string): Promise<TabCounts | null> => {
	try {
		const qs = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
		const res = await fetch(`${BACKEND_URL}/api/reportTabCounts${qs}`, {
			credentials: 'include',
		});
		if (!res.ok) return null;
		return (await res.json()) as TabCounts;
	} catch {
		return null;
	}
};

// ── Transition rules ────────────────────────────────────────────────────────
// A report has to be activated before it can be closed, so that the reporter
// always gets the "someone has started on this" signal. Forwarding is allowed
// from either open state and hands the report over as *received*.
export const canActivate = (tab: ReportTab): boolean => tab === 'received';
export const canCloseFrom = (tab: ReportTab): boolean => tab === 'progress';
export const canForwardFrom = (tab: ReportTab): boolean =>
	tab === 'received' || tab === 'progress';

/**
 * Which drops a kanban column accepts, by COLUMN PAIR.
 *
 * Whether a given card may leave its column at all is a separate, per-card
 * question answered at the drag source (`isCardDraggable`): a Forwarded card is
 * another authority's work, and a Closed card may only be moved by whoever
 * closed it.
 */
export const isDropAllowed = (from: ReportTab, to: ReportTab): boolean => {
	if (from === to) return false;
	if (from === 'active') return false; // someone else holds it now
	if (to === 'received') return false; // no un-activating
	// Closed → In Progress is the reopen action; the source gate makes sure only
	// the authority who closed it can start that drag.
	if (to === 'progress') return from === 'received' || from === 'closed';
	if (to === 'closed') return from === 'progress';
	if (to === 'active') return from === 'received' || from === 'progress';
	return false;
};

/**
 * May THIS card be picked up?
 *
 * Received and In Progress are mine to move. Forwarded is not. Closed is only
 * mine if I am the one who closed it — which is also why a "Closed by X" tag is
 * shown *only* when someone else closed it: the presence of that tag is the
 * at-a-glance signal that the card is not draggable.
 */
export const isCardDraggable = (
	tab: ReportTab,
	closedByUserId: number | null | undefined,
	currentUserId: number | null | undefined
): boolean => {
	if (tab === 'received' || tab === 'progress') return true;
	if (tab === 'closed') return closedByMe(closedByUserId, currentUserId);
	return false;
};

/** Did the viewing authority close this report themselves? */
export const closedByMe = (
	closedByUserId: number | null | undefined,
	currentUserId: number | null | undefined
): boolean =>
	closedByUserId != null &&
	currentUserId != null &&
	Number(closedByUserId) === Number(currentUserId);

// ── Search highlighting ─────────────────────────────────────────────────────
// Same treatment as the News/Explore feeds so a match reads identically
// wherever the user finds it.
export const renderHighlightedText = (text: any, query: string) => {
	const safeText = text ? String(text) : '';
	const trimmedQuery = query.trim();
	if (!trimmedQuery) return <span>{safeText}</span>;
	try {
		const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const regex = new RegExp(`(${escapedQuery})`, 'gi');
		const parts = safeText.split(regex);
		return (
			<>
				{parts.map((part, i) =>
					part.toLowerCase() === trimmedQuery.toLowerCase() ? (
						<span key={i} className='bg-blue-200 text-blue-800 rounded px-0.5 font-bold'>
							{part}
						</span>
					) : (
						<span key={i}>{part}</span>
					)
				)}
			</>
		);
	} catch (e) {
		return <span>{safeText}</span>;
	}
};

/**
 * The authority's own bookkeeping number, shown at the END of the title line.
 *
 * `shrink-0` is the whole point: when the title is long enough to ellipsize, the
 * number must survive — finding a report by its ERP number is exactly the case
 * where the title is least useful.
 */
export const ReportNumberChip = ({
	number,
	searchQuery = '',
	className = '',
}: {
	number?: string | null;
	searchQuery?: string;
	className?: string;
}) => {
	if (!number) return null;
	return (
		<span
			className={`shrink-0 inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 font-mono text-[9px] sm:text-[11px] font-semibold leading-none text-indigo-700 ${className}`}
			title={`Report number: ${number}`}
		>
			#{renderHighlightedText(number, searchQuery)}
		</span>
	);
};

// Placeholder shown in the single-report view when no number has been assigned
// yet, so the affordance is discoverable instead of invisible.
export const NO_NUMBER_LABEL = 'No number';

/**
 * A horizontal row you pan by dragging, with the scrollbar hidden and both ends
 * faded when there is more content that way — the same affordance the phone
 * app's report tab bar uses.
 *
 * Only MOUSE pointers are hijacked for the drag. Touch keeps native scrolling:
 * capturing a touch pointer here would give a worse, non-inertial pan than the
 * browser's own, and would fight vertical page scrolling that starts on the bar.
 *
 * A drag that moves more than a few pixels swallows the click that follows it,
 * so panning past a tab never selects it.
 */
export const DragScrollRow = ({
	children,
	className = '',
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [fadeLeft, setFadeLeft] = useState(false);
	const [fadeRight, setFadeRight] = useState(false);
	const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });

	const updateFades = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		const max = el.scrollWidth - el.clientWidth;
		setFadeLeft(el.scrollLeft > 1);
		setFadeRight(el.scrollLeft < max - 1);
	}, []);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		updateFades();
		// Content width changes when the counts land (e.g. "Received" →
		// "Received (12)"), which can flip the row from fitting to scrollable.
		const ro = new ResizeObserver(updateFades);
		ro.observe(el);
		for (const child of Array.from(el.children)) ro.observe(child);
		window.addEventListener('resize', updateFades);
		return () => {
			ro.disconnect();
			window.removeEventListener('resize', updateFades);
		};
	}, [updateFades, children]);

	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		if (e.pointerType !== 'mouse' || e.button !== 0) return;
		const el = ref.current;
		if (!el) return;
		drag.current = {
			active: true,
			startX: e.clientX,
			startScroll: el.scrollLeft,
			moved: 0,
		};
	};

	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		const el = ref.current;
		if (!drag.current.active || !el) return;
		const dx = e.clientX - drag.current.startX;
		drag.current.moved = Math.max(drag.current.moved, Math.abs(dx));
		// Capture only once the gesture is clearly a pan, so a plain click on a
		// tab still behaves like a click.
		if (drag.current.moved > 4 && el.hasPointerCapture?.(e.pointerId) !== true) {
			el.setPointerCapture?.(e.pointerId);
		}
		el.scrollLeft = drag.current.startScroll - dx;
		// Setting scrollLeft fires `scroll` asynchronously, so relying on that
		// alone leaves the edge fades a frame or more behind the drag. Recompute
		// inline; the state setters no-op when nothing actually changed.
		updateFades();
	};

	const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
		const el = ref.current;
		if (el?.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
		drag.current.active = false;
	};

	return (
		<div
			ref={ref}
			className={`drag-scroll-row overflow-x-auto ${className}`}
			style={{
				// Fade only the end that actually has more content behind it.
				maskImage: `linear-gradient(to right, transparent 0px, black ${
					fadeLeft ? '28px' : '0px'
				}, black calc(100% - ${fadeRight ? '28px' : '0px'}), transparent 100%)`,
				WebkitMaskImage: `linear-gradient(to right, transparent 0px, black ${
					fadeLeft ? '28px' : '0px'
				}, black calc(100% - ${fadeRight ? '28px' : '0px'}), transparent 100%)`,
				cursor: drag.current.active ? 'grabbing' : undefined,
			}}
			onScroll={updateFades}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={endDrag}
			onPointerCancel={endDrag}
			onClickCapture={(e) => {
				// Swallow the click that terminates a pan.
				if (drag.current.moved > 4) {
					e.preventDefault();
					e.stopPropagation();
					drag.current.moved = 0;
				}
			}}
		>
			{children}
		</div>
	);
};

/**
 * The list ⇄ Interactive Dashboard switch.
 *
 * Rendered by the PARENT, outside the branch that picks a view, and anchored to
 * the viewport — so it occupies the identical spot in both modes and switching
 * doesn't make the control jump out from under the cursor. It sits in the empty
 * left gutter, which is where the other pages put their view-level controls
 * (the feed's category filter, Explore's map toggle), rather than stealing a row
 * above the tabs.
 *
 * Desktop only: the board needs four side-by-side columns and a pointer, so
 * below `lg` there is nothing to switch to.
 */
export const ViewModeSwitch = ({
	isKanban,
	onChange,
}: {
	isKanban: boolean;
	onChange: (kanban: boolean) => void;
}) => (
	<div className='fixed left-6 top-[calc(var(--header-height,80px)+24px)] z-40 hidden lg:block'>
		<div
			onClick={() => onChange(!isKanban)}
			className='flex cursor-pointer items-center select-none outline-none focus:outline-none focus:ring-0 focus-visible:outline-none'
			title={isKanban ? 'Switch to List View' : 'Switch to Interactive Dashboard'}
			role='button'
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onChange(!isKanban);
				}
			}}
		>
			{/* Circular Icon Button */}
			<div
				className='relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#004a99] text-white shadow-lg'
				aria-label={isKanban ? 'Switch to list view' : 'Switch to Interactive Dashboard'}
			>
				{isKanban ? <LayoutGrid className='h-6 w-6' /> : <LayoutList className='h-6 w-6' />}
			</div>

			{/* Flag Banner coming out of the button */}
			<div className='-ml-5 flex h-9 items-center rounded-r-full border border-l-0 border-blue-400/30 bg-[#004a99] pl-7 pr-4 text-xs font-semibold tracking-wide text-white shadow-md'>
				<span className='whitespace-nowrap'>
					{isKanban ? 'Interactive Dashboard' : 'List View'}
				</span>
			</div>
		</div>
	</div>
);
